import { Button, EmptyState } from '@chopchop-se/ui';
import type { ClaimPhoto, ClaimPhotoCategory } from '@warehouse/shared';
import { Camera, ExternalLink, Trash2 } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useConfirm } from '../../../components/ConfirmDialog';
import { apiUrl } from '../../../lib/api-client';

/**
 * Photo evidence, grouped by category. Cold-chain claims need a signed freight
 * note AND a damaged-goods photo — the hint is rendered here, but the RULE
 * lives on the server (the old WMS enforced it in the form only).
 */
export function ClaimPhotoGrid({
  claimId,
  photos,
  canEdit,
  coldChain,
  onUpload,
  onRemove,
  uploading,
}: {
  claimId: string;
  photos: readonly ClaimPhoto[];
  canEdit: boolean;
  coldChain: boolean;
  onUpload?: (file: File, category: ClaimPhotoCategory) => void;
  onRemove?: (fileId: string) => void;
  uploading?: boolean;
}): ReactNode {
  const { t } = useTranslation(['claims', 'common']);
  const inputRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<ClaimPhotoCategory>('damaged_goods');
  const { confirm, dialog } = useConfirm();

  const categories: ClaimPhotoCategory[] = ['freight_note', 'damaged_goods', 'other'];

  return (
    <section aria-label={t('claims:photo.title')}>
      {coldChain ? (
        <p className="mb-3 rounded-xl bg-warning/10 px-3 py-2 text-sm text-ink-light">
          {t('claims:photo.coldChainRequired')}
        </p>
      ) : null}

      {photos.length === 0 ? (
        <EmptyState bare icon={<Camera />} title={t('claims:photo.empty')} />
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {photos.map((photo) => (
            <li key={photo.id} className="overflow-hidden rounded-xl border border-surface-border">
              <a
                href={apiUrl(`/claims/${claimId}/photos/${photo.id}`)}
                target="_blank"
                rel="noreferrer"
                title={t('claims:photo.open')}
                className="block"
              >
                <img
                  src={apiUrl(`/claims/${claimId}/photos/${photo.id}`)}
                  alt={photo.name}
                  loading="lazy"
                  className="aspect-4/3 w-full object-cover"
                />
              </a>
              <div className="flex items-center justify-between gap-2 px-2 py-1.5">
                <span className="truncate text-xs text-ink-muted">
                  {t(`claims:photo.categories.${photo.category}`)}
                </span>
                <span className="flex items-center gap-1">
                  <a
                    href={apiUrl(`/claims/${claimId}/photos/${photo.id}`)}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={t('claims:photo.open')}
                    className="text-ink-muted hover:text-ink"
                  >
                    <ExternalLink className="size-3.5" aria-hidden />
                  </a>
                  {canEdit && onRemove ? (
                    <button
                      type="button"
                      aria-label={`${t('common:actions.remove')} ${photo.name}`}
                      className="text-ink-muted hover:text-danger"
                      onClick={() => {
                        void (async () => {
                          const confirmed = await confirm({
                            title: t('claims:photo.removeTitle'),
                            message: t('claims:photo.removeMessage'),
                            confirmLabel: t('common:actions.remove'),
                          });
                          if (confirmed) onRemove?.(photo.id);
                        })();
                      }}
                    >
                      <Trash2 className="size-3.5" aria-hidden />
                    </button>
                  ) : null}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {canEdit && onUpload ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <label className="text-sm text-ink-light" htmlFor={`photo-category-${claimId}`}>
            {t('claims:photo.category')}
          </label>
          <select
            id={`photo-category-${claimId}`}
            className="rounded-lg border border-surface-border bg-surface px-2 py-1.5 text-sm"
            value={category}
            onChange={(event) => {
              setCategory(event.target.value as ClaimPhotoCategory);
            }}
          >
            {categories.map((value) => (
              <option key={value} value={value}>
                {t(`claims:photo.categories.${value}`)}
              </option>
            ))}
          </select>

          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            aria-label={t('claims:photo.add')}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onUpload(file, category);
              event.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            icon={<Camera />}
            loading={uploading ?? false}
            onClick={() => inputRef.current?.click()}
          >
            {t('claims:photo.add')}
          </Button>
        </div>
      ) : null}

      {dialog}
    </section>
  );
}
